import { useCallback, useEffect, useId, useState } from 'react';
import { useI18n } from '../../i18n';
import { useAnnounce } from '../../ui/a11y';
import { Pill } from '../../ui/DataBits';
import { AuditPage, AuditRow, csvDownload, errorText, fmtDateTime, rpc } from './api';
import { activityActor, activityLabel, activityTarget } from './activityLabel';

// One audit log view for the school's Activity tab, a person's activity and
// the Platform log. With source="school" it reads the caller's own school
// through school_audit_log (heads of school and school admins, 0015). Newest first, filter by action and dates, "Load more",
// CSV export of what's loaded (cells escaped against spreadsheet formulas),
// and a hash-chain check.
export function AuditList({
  scope,
  schoolId = null,
  targetId = null,
  title,
  compact = false,
  showSchool = false,
  source = 'platform',
  intro,
}: {
  scope: 'platform' | 'school' | 'all';
  schoolId?: string | null;
  targetId?: string | null;
  title: string;
  compact?: boolean;
  showSchool?: boolean;
  source?: 'platform' | 'school';
  intro?: string;
}) {
  const { t, locale } = useI18n();
  const announce = useAnnounce();
  const id = useId();
  const [rows, setRows] = useState<AuditRow[] | null>(null);
  const [actions, setActions] = useState<string[]>([]);
  const [next, setNext] = useState<number | null>(null);
  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [verify, setVerify] = useState<{ ok: boolean; rows_checked: number; broken_at_id?: number } | null>(null);

  const load = useCallback(
    async (before: number | null) => {
      const filters = {
        p_action: action || null,
        p_from: from ? new Date(`${from}T00:00:00`).toISOString() : null,
        p_to: to ? new Date(new Date(`${to}T00:00:00`).getTime() + 86400000).toISOString() : null,
        p_before_id: before,
        p_limit: compact ? 20 : 50,
        p_target_id: targetId,
      };
      const r =
        source === 'school'
          ? await rpc<AuditPage>('school_audit_log', filters)
          : await rpc<AuditPage>('platform_audit_log', { p_scope: scope, p_school_id: schoolId, ...filters });
      if (r.error) {
        setError(errorText(t, r.error));
        return;
      }
      setError(null);
      setRows((prev) => (before && prev ? [...prev, ...r.data.rows] : r.data.rows));
      setNext(r.data.next_before);
      setActions(r.data.actions);
      if (before) announce(t['pa.logMoreLoaded'](r.data.rows.length));
    },
    [scope, schoolId, action, from, to, targetId, compact, source, t, announce],
  );

  useEffect(() => {
    load(null);
  }, [load]);

  async function check() {
    const r = await rpc<{ ok: boolean; rows_checked: number; broken_at_id?: number }>('audit_log_verify', {
      p_school_id: scope === 'school' ? schoolId : null,
    });
    if (r.error) return setError(errorText(t, r.error));
    setVerify(r.data);
    announce(r.data.ok ? t['pa.chainOk'](r.data.rows_checked) : t['pa.chainBroken'](r.data.broken_at_id ?? 0));
  }

  function exportCsv() {
    if (!rows) return;
    csvDownload(
      `swipe-${scope === 'school' ? 'school' : 'platform'}-log-${new Date().toISOString().slice(0, 10)}.csv`,
      [t['pa.colTime'], t['admin.colSchool'], t['pa.colWho'], t['pa.colRole'], t['pa.colAction'], t['pa.colTarget'], t['pa.colDetails']],
      rows.map((r) => [r.occurred_at, r.school_code ?? '', r.actor, r.acting_role ?? '', r.action, r.target ?? '', JSON.stringify(r.details)]),
    );
  }

  const summarize = (d: Record<string, unknown>) =>
    Object.entries(d)
      .filter(([, v]) => v !== null && v !== '' && typeof v !== 'object')
      .slice(0, 4)
      .map(([k, v]) => `${k}: ${String(v)}`)
      .join(' · ');

  return (
    <section className="panel p5" aria-labelledby={`${id}-t`}>
      <div className="panel-head wrap">
        <h2 className="panel-title" id={`${id}-t`}>
          {title}
        </h2>
        {!compact && (
          <div className="filters">
            <span className="select-wrap">
              <select className="select" aria-label={t['pa.filterAction']} value={action} onChange={(e) => setAction(e.target.value)}>
                <option value="">{t['pa.allActions']}</option>
                {actions.map((a) => (
                  <option key={a} value={a}>
                    {source === 'school' ? activityLabel(t, a) : a}
                  </option>
                ))}
              </select>
            </span>
            <label className="filter-date">
              <span>{t['pa.from']}</span>
              <input className="field" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label className="filter-date">
              <span>{t['pa.to']}</span>
              <input className="field" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </label>
          </div>
        )}
      </div>
      {intro && <p className="form-hint">{intro}</p>}
      {!compact && (
        <div className="form-actions">
          <button type="button" className="btn btn-ghost" onClick={exportCsv} disabled={!rows?.length}>
            {t['pa.exportCsv']}
          </button>
          {source === 'platform' && (
            <button type="button" className="btn btn-ghost" onClick={check}>
              {t['pa.verifyChain']}
            </button>
          )}
          {verify && (
            <Pill kind={verify.ok ? 'good' : 'crit'}>
              {verify.ok ? t['pa.chainOk'](verify.rows_checked) : t['pa.chainBroken'](verify.broken_at_id ?? 0)}
            </Pill>
          )}
        </div>
      )}
      {error ? (
        <p className="error-text" role="alert">
          {error}
        </p>
      ) : !rows ? (
        <p role="status" className="text-muted">
          {t.loading}
        </p>
      ) : rows.length === 0 ? (
        <p className="text-muted">{t['pa.logEmpty']}</p>
      ) : (
        <>
          <ol className="log-list">
            {rows.map((r) => (
              <li key={r.id}>
                <span className="log-time">{fmtDateTime(locale, r.occurred_at)}</span>
                <span className="log-main">
                  <strong>{source === 'school' ? activityLabel(t, r.action, r.details) : r.action}</strong>
                  {source === 'school' ? activityTarget(r.target) && <> · {activityTarget(r.target)}</> : r.target && <> · {r.target}</>}
                  {showSchool && r.school_code && <span className="code-chip">{r.school_code}</span>}
                </span>
                <span className="log-who">
                  {source === 'school' ? activityActor(t, r.actor, r.acting_role) : r.actor}
                  {source !== 'school' && r.acting_role && ` (${r.acting_role})`}
                </span>
                {source !== 'school' && Object.keys(r.details ?? {}).length > 0 && <span className="log-details">{summarize(r.details)}</span>}
              </li>
            ))}
          </ol>
          {next && (
            <button type="button" className="btn btn-ghost" onClick={() => load(next)}>
              {t['pa.loadMore']}
            </button>
          )}
        </>
      )}
    </section>
  );
}
